import { Experiment, FinnWindow, InitialTrackerState, PulseInitWrapper } from './types';
import { cleanUrl } from './urls';

export class Config {
    constructor(readonly window: FinnWindow) {}
}

export default class FinnPulseInitializer {
    private readonly pulse: PulseInitWrapper;
    private readonly win: FinnWindow;

    private readonly isDev: boolean;
    private readonly providerId: string;

    private static readonly dataAppName = 'data-application-name';
    private static readonly pageStateTrackerName = 'pageStateTracker';
    private static trackerEventSchemaVersion = 'http://schema.schibsted.com/events/tracker-event.json/267.json';

    public constructor(isDev: boolean, pulse: PulseInitWrapper, config?: Config) {
        this.isDev = isDev;
        this.pulse = pulse;

        if (config && config.window) {
            this.win = config.window;
        } else {
            this.win = window;
        }

        // We're using temporary providers during the implementation
        // of the new Pulse tagging plan. The '-tmp' suffix should be
        // removed when we do the "global" switch from finn-analytics-js.
        if (!this.isDev) {
            this.providerId = 'finn-tmp';
        } else {
            this.providerId = 'finndev-tmp';
            this.win.document.cookie = '_pulse2debug=true;SameSite=Lax';
        }

        this.initializeTracker();
    }

    private initializeTracker(): void {
        const initialTrackerState = this.generateInitialTrackerState();
        this.pulse.init(this.providerId, {}, initialTrackerState);
        this.pulse.init(this.providerId, {}, initialTrackerState, FinnPulseInitializer.pageStateTrackerName);
    }

    private generateInitialTrackerState(): InitialTrackerState {
        // Provider and deployTag - static.
        const initialTrackerState: InitialTrackerState = {
            // We want to set the schema version explicitly,
            // and not use the default (and lagging) values from
            // https://github.schibsted.io/spt-dataanalytics/pulse-event-builders.
            // If we make any schema change in the event-formats repository
            // (and by that bumping the schema versions),
            // we easily want to be able to bump the schema version here,
            // so that events validate when using the schema validator.
            schema: FinnPulseInitializer.trackerEventSchemaVersion,
            deployTag: `finn-pulse-init-PACKAGE_VERSION`,
            provider: {
                product: 'www.finn',
                productType: 'ResponsiveWeb',
            },
        };

        // deployStage - Implicit 'pro' if not set, dev otherwise.
        if (this.isDev) {
            initialTrackerState.deployStage = 'dev';
        }

        // Provider - Application name (if available).
        const applicationName = this.getApplicationName();
        if (applicationName) {
            initialTrackerState.provider.component = applicationName;
        }

        // Device - Finn device id.
        const finnDeviceId = this.getFinnDeviceId();
        if (finnDeviceId && finnDeviceId !== '') {
            initialTrackerState.device = {
                additionalDeviceIds: [
                    {
                        '@id': `sdrn:${this.providerId}:device:id:${finnDeviceId}`,
                        component: this.providerId,
                    },
                ],
            };
        }

        // Actor - Schibsted Account Id & Finn login id.
        const schAccountId = this.getSchibstedAccountId();
        if (schAccountId && schAccountId !== '') {
            initialTrackerState.actor = {
                id: schAccountId,
                realm: 'spid.no',
            };

            const finnLoginId = this.getFinnLoginId();
            if (finnLoginId && finnLoginId !== '') {
                initialTrackerState.actor.additionalIds = [
                    {
                        '@id': `sdrn:${this.providerId}:user:${finnLoginId}`,
                        component: this.providerId,
                    },
                ];
            }
        }

        // Experiments - A/B Test data from Unleash cookie.
        const experiments = this.getExperiments();
        if (experiments && experiments.length > 0) {
            initialTrackerState.experiments = experiments;
        }

        // Object - clean selected URLs.
        const url = this.win.document.URL;
        const urlCleaned = cleanUrl(url);
        if (urlCleaned) {
            initialTrackerState.object = {
                url: urlCleaned,
            };
        }

        return initialTrackerState;
    }

    private getApplicationName(): string | null {
        return this.getAttributeValue(FinnPulseInitializer.dataAppName);
    }

    private getFinnDeviceId(): string | null {
        return this.getCookie('USERID');
    }

    private getSchibstedAccountId(): string | undefined {
        if (this.win.pulse2opt && this.win.pulse2opt.userId) {
            return this.win.pulse2opt.userId;
        }
    }

    private getFinnLoginId(): string | undefined {
        if (this.win.finnUserId) {
            return this.win.finnUserId;
        }
    }

    private getExperiments(): Experiment[] | undefined {
        const abTestData = this.getCookie('abtest');

        if (!abTestData || abTestData === '' || abTestData.charAt(0) !== '{') {
            return;
        }

        let abTestJson;
        try {
            abTestJson = JSON.parse(abTestData);
        } catch (e) {
            return;
        }

        const experiments: Experiment[] = [];

        Object.keys(abTestJson).forEach((key) => {
            if (!key) {
                return;
            }

            // We currently can't reliably deduce the variant from
            // the value found in the 'abtest' cookie, since '_'
            // also sometimes is used in toggle names. Therefore (for now)
            // set variant to an empty string (it's required in the schema)
            // and use the complete value in the 'id'.
            experiments.push({
                platform: this.providerId,
                id: key,
                variant: '',
            });
        });

        return experiments;
    }

    private getCookie(key: string): string | null {
        if (!key || !this.win.document.cookie) {
            return null;
        }
        return (
            decodeURIComponent(
                this.win.document.cookie.replace(
                    new RegExp(
                        `(?:(?:^|.*;)\\s*${encodeURIComponent(key).replace(
                            /[-.+*]/g,
                            '\\$&',
                        )}\\s*\\=\\s*([^;]*).*$)|^.*$`,
                    ),
                    '$1',
                ),
            ) || null
        );
    }

    private getAttributeValue(attributeName: string): string | null {
        try {
            let attributeValue;
            if (this.win.document.currentScript) {
                attributeValue = this.win.document.currentScript.getAttribute(attributeName);
            }
            if (!attributeValue) {
                const el = this.win.document.querySelector(`[${attributeName}]`);
                if (el) {
                    attributeValue = el.getAttribute(attributeName);
                }
            }
            return attributeValue ?? null;
        } catch (e) {
            return null;
        }
    }
}
